// src/pages/modals/CreateOrderModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "../../api/axios";
import "./CreateOrderModal.css";

export default function CreateOrderModal({ locale = "kz", token, onClose }) {
  const [page, setPage] = useState(1);
  const [groups, setGroups] = useState([]);           // текущая страница
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // сохраняем выбор между страницами

  // шаг 2 — форма генерации
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", title2: "", description: "" });
  const [errors, setErrors] = useState({});

  const t = useMemo(
    () =>
      ({
        kz: {
          title: "Бұйрық жасау",
          loading: "Жүктелуде...",
          close: "Жабу",
          createForSelected: "Таңдалғандарға бұйрық құру",
          empty: "Топтар табылмады",
          selectedCount: (n) => `Таңдалғаны: ${n}`,
          selectAll: "Осы беттегінің бәрін таңдау",
          clearSel: "Тазалау",
          step2Title: "Бұйрық деректері",
          f1: "Бұйрық атауы",
          f2: "Қосымша атау",
          f3: "Сипаттама",
            f1ph: "Алгебра пәні бойынша сараптама",          // 🆕 placeholders
        f2ph: "5–6 сынып",
        f3ph: "Тақырыптық жоспар бекітілді.",
          back: "Артқа",
          generate: "Бұйрық жасау",
          required: "Бұл өріс міндетті",
          success: "Бұйрық(тар) сәтті жасалды",
          loadErr: "Топтарды жүктеу қатесі",
          genErr: "Бұйрық жасау сәтсіз аяқталды",
          colOrg: "Баспа",
          colTitle: "Атауы",
          prev: "Алдыңғы",
          next: "Келесі",
        },
        ru: {
          title: "Создание приказа",
          loading: "Загрузка...",
          close: "Закрыть",
          createForSelected: "Создать приказ для выбранных",
          empty: "Нет доступных групп",
          selectedCount: (n) => `Выбрано: ${n}`,
          selectAll: "Выбрать всё на странице",
          clearSel: "Очистить",
          step2Title: "Данные приказа",
          f1: "Название приказа",
          f2: "Доп. название",
          f3: "Описание",
           f1ph: "Экспертиза по предмету Алгебра",           // 🆕 placeholders
        f2ph: "5–6 классы",
        f3ph: "Тематический план утверждён.",
          back: "Назад",
          generate: "Сформировать приказ",
          required: "Поле обязательно",
          success: "Приказы успешно сформированы",
          loadErr: "Ошибка загрузки групп",
          genErr: "Не удалось сформировать приказы",
          colOrg: "Организация",
          colTitle: "Название",
          prev: "Назад",
          next: "Вперёд",
        },
      }[locale]),
    [locale]
  );

  // загрузка списка (пагинация с бэка)
  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/orders/groups", {
          headers: { Authorization: `Bearer ${token}` },
          params: { page },
        });
        const d = res.data?.data || {};
        // если бэк вдруг вернул лишнее — фильтруем на всякий
        const pageItems = (d.data || []).filter(
          (g) => String(g.status || "").trim().toLowerCase() === "приказға дайын"
        );
        setGroups(pageItems);
        setMeta({
          current_page: Number(d.current_page || page),
          last_page: Number(d.last_page || 1),
          total: Number(d.total || pageItems.length),
        });
      } catch (e) {
      
        alert(t.loadErr);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [page, token, t.loadErr]);

  // helpers выбора
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const pageAllIds = groups.map((g) => g.id);
  const isAllOnPageChecked = pageAllIds.length > 0 && pageAllIds.every((id) => selected.has(id));

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (isAllOnPageChecked) {
        pageAllIds.forEach((id) => next.delete(id));
      } else {
        pageAllIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const openForm = () => {
    if (selected.size === 0) return;
    setShowForm(true);
  };

  const onChangeForm = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = t.required;
    if (!form.title2.trim()) e.title2 = t.required;
    if (!form.description.trim()) e.description = t.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleGenerate = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const group_ids = Array.from(selected);
      await axios.post(
        "/orders/generate",
        {
          group_ids,
          title: form.title.trim(),
          title2: form.title2.trim(),
          description: form.description.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // локально убираем созданные (и с других страниц тоже, если они на текущей)
      setGroups((gs) => gs.filter((g) => !selected.has(g.id)));
      setSelected(new Set());
      setShowForm(false);
      setForm({ title: "", title2: "", description: "" });
      alert(t.success);
      // перезагрузим страницу, чтобы числа пагинации были актуальны
      setPage(1);
    } catch (e) {
     
      alert(t.genErr);
    } finally {
      setBusy(false);
    }
  };

  // пагинация
  const go = (p) => {
    if (p < 1 || p > meta.last_page || p === page) return;
    setPage(p);
  };

  // рисуем компактные номера страниц
  const pageNumbers = (() => {
    const arr = [];
    const { last_page } = meta;
    const win = 2; // по 2 слева/справа
    const start = Math.max(1, page - win);
    const end = Math.min(last_page, page + win);
    for (let i = start; i <= end; i++) arr.push(i);
    if (start > 1) arr.unshift(1, "...");
    if (end < last_page) arr.push("...", last_page);
    return arr;
  })();

  return (
    <div className="co-backdrop">
      <div className="co-modal">
        <button className="co-close-btn" onClick={onClose} aria-label="Close">×</button>
        <h2 className="co-title">{t.title}</h2>

        {/* Панель выбора */}
        {!loading && (
          <div className="co-selectbar">
            <label className="co-check">
              <input
                type="checkbox"
                checked={isAllOnPageChecked}
                onChange={toggleAllOnPage}
                disabled={busy || showForm || groups.length === 0}
              />
              <span>{t.selectAll}</span>
            </label>

            <span className="co-selected-count">{t.selectedCount(selected.size)}</span>

            <div className="co-spacer" />

            <button
              className="co-btn-secondary"
              onClick={clearSelection}
              disabled={busy || selected.size === 0 || showForm}
            >
              {t.clearSel}
            </button>

            <button
              className="co-btn-primary"
              onClick={openForm}
              disabled={busy || selected.size === 0}
            >
              {t.createForSelected}
            </button>
          </div>
        )}

        {/* Таблица только organization_name и title */}
        {loading ? (
          <p className="co-loading">{t.loading}</p>
        ) : groups.length === 0 ? (
          <p className="co-empty">{t.empty}</p>
        ) : (
          <>
            <table className={`co-table ${showForm ? "co-disabled" : ""}`}>
              <thead>
                <tr>
                  <th style={{ width: 44 }}></th>
                  <th>{t.colOrg}</th>
                  <th>{t.colTitle}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <label className="co-check">
                        <input
                          type="checkbox"
                          checked={selected.has(g.id)}
                          onChange={() => toggleOne(g.id)}
                          disabled={busy || showForm}
                        />
                        <span className="co-checkmark" />
                      </label>
                    </td>
                    <td>{g.organization_name || "—"}</td>
                    <td>{g.title}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Пагинация */}
            <div className="co-pagination">
              <button className="co-page-btn" onClick={() => go(page - 1)} disabled={page <= 1}>
                {t.prev}
              </button>
              {pageNumbers.map((p, i) =>
                p === "..." ? (
                  <span key={`el-${i}`} className="co-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`co-page-num ${p === page ? "active" : ""}`}
                    onClick={() => go(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="co-page-btn"
                onClick={() => go(page + 1)}
                disabled={page >= meta.last_page}
              >
                {t.next}
              </button>
            </div>
          </>
        )}

        {/* ШАГ 2: форма */}
        {showForm && (
          <div className="co-form-backdrop">
            <div className="co-form">
              <h3 className="co-form-title">{t.step2Title}</h3>

              <label className="co-input">
  <span>{t.f1}</span>
  <input
    type="text"
    value={form.title}
    onChange={(e) => onChangeForm("title", e.target.value)}
    disabled={busy}
    placeholder={t.f1ph}
  />
  {errors.title && <em className="co-error">{errors.title}</em>}
</label>

<label className="co-input">
  <span>{t.f3}</span>
  <textarea
    rows={4}
    value={form.description}
    onChange={(e) => onChangeForm("description", e.target.value)}
    disabled={busy}
    placeholder={t.f3ph}
  />
  {errors.description && <em className="co-error">{errors.description}</em>}
</label>

<label className="co-input">
  <span>{t.f2}</span>
  <input
    type="text"
    value={form.title2}
    onChange={(e) => onChangeForm("title2", e.target.value)}
    disabled={busy}
    placeholder={t.f2ph}
  />
  {errors.title2 && <em className="co-error">{errors.title2}</em>}
</label>

              <div className="co-form-actions">
                <button className="co-btn-secondary" onClick={() => setShowForm(false)} disabled={busy}>
                  {t.back}
                </button>
                <button className="co-btn-primary" onClick={handleGenerate} disabled={busy}>
                  {t.generate}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
